/**
 * @file queryBounds.ts
 * @path packages/config/src/firebase/queryBounds.ts
 * @description The one place that decides how many documents a list read may
 * return.
 *
 * P2.2: `FirestoreService.getAll`/`.query` issued unbounded reads, so every
 * caller paid for the whole collection — the website's portfolio and academy
 * lists, and every admin table. Rather than patch a `limit()` onto each call
 * site and wait for the next one to forget, the shared service appends a
 * default bound whenever the caller did not supply one. Call sites that know
 * their page size still pass it, and this only ever caps them.
 */

/** Documents a list read returns when the caller names no page size. */
export const DEFAULT_LIST_LIMIT = 100;

/** Page size for the public content lists (portfolio and academy indexes). */
export const CONTENT_PAGE_SIZE = 24;

/** Page size for the admin tables. */
export const ADMIN_PAGE_SIZE = 50;

/** The `type` values Firestore's query constraints use to bound a result set. */
const LIMIT_TYPES = new Set(["limit", "limitToLast"]);

export const hasLimitConstraint = (
  constraints: ReadonlyArray<{ type?: string } | null | undefined>,
): boolean => constraints.some((c) => !!c && LIMIT_TYPES.has(String(c.type)));

/**
 * Returns the constraints unchanged when they already bound the read, and
 * otherwise appends `makeLimit(max)`.
 *
 * Pure and SDK-agnostic on purpose: `makeLimit` is the caller's `limit()` so
 * this stays testable without a Firestore instance.
 */
export const withDefaultLimit = <T extends { type?: string }>(
  constraints: ReadonlyArray<T>,
  makeLimit: (n: number) => T,
  max: number = DEFAULT_LIST_LIMIT,
): T[] =>
  hasLimitConstraint(constraints)
    ? [...constraints]
    : [...constraints, makeLimit(max)];
